import type { BillingFormValues } from "@gik/checkout/components/BillingForm/BillingForm";
import { BillingAddressFormSchema } from "@gik/core/form/schemas/BillingAddressFormSchema";
import type { BillingAddress } from "@gik/core/models/gik/BillingAddress";
import type { BaseUserWithLocation } from "@gik/core/models/gik/User";
import type { UIComponent } from "@gik/core/types/UI";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { renderPortal } from "@gik/core/utils/RenderPortal";
import { Button } from "@gik/ui-legacy/Button";
import { createZipExistenceValidate, Form } from "@gik/ui-legacy/Form";
import type { FormikProps } from "formik";
import type React from "react";

import { BillingAddressFieldSet } from "./BillingAddressFieldSet";

export type BillingAddressFormProps = {
	user?: BaseUserWithLocation;
	billingAddress?: BillingAddress;
	buttonsPortal?: React.MutableRefObject<HTMLElement>;
	buttons?: (formRenderProps: FormikProps<object>) => React.ReactNode;
	onCancel?: () => void;
	onSubmit?: (values: BillingFormValues) => void;
} & UIComponent;

export function BillingAddressForm({
	children,
	className,
	user,
	billingAddress,
	buttons,
	buttonsPortal,
	onSubmit,
	onCancel,
	...otherProps
}: React.PropsWithChildren<BillingAddressFormProps>): React.ReactElement {
	const bem = useBemCN("billing-address-form");

	const initialValues = {
		firstName: billingAddress?.firstName || user?.firstName,
		lastName: billingAddress?.lastName || user?.lastName,
		state: billingAddress?.state || user?.state,
		city: billingAddress?.city || user?.city,
		companyName: billingAddress?.companyName,
		address1: billingAddress?.address1,
		address2: billingAddress?.address2,
		postalCode: billingAddress?.postalCode,
		phone: billingAddress?.phone,
		// TODO: problems with validation
		country: billingAddress?.country,
	};

	const formId = "BillingAddressForm";
	const schema = BillingAddressFormSchema();

	return (
		<div {...bem(null, null, className)} {...otherProps}>
			<Form
				schema={schema}
				validate={createZipExistenceValidate(schema)}
				vertical
				id={formId}
				restoreAfterUpdate
				onSubmit={onSubmit}
				initialValues={initialValues}
				variant="default-solid"
				render={({ isSubmitting, isValid }) => {
					const buttons = (
						<Button
							fullWidth
							{...bem("btnSubmit")}
							form={formId}
							type="submit"
							loading={isSubmitting}
							disabled={!isValid}
						>
							Save
						</Button>
					);

					return (
						<>
							<BillingAddressFieldSet />
							{renderPortal(buttons, () => buttonsPortal?.current)}
						</>
					);
				}}
			></Form>
		</div>
	);
}
